import React from 'react';
import { BodyText, ButtonDropdown, Icon, IconSize, IconType, Tooltip } from 'ui-toolkit';
import { ePermissions } from '../../../../types/administration/userAdministration/ePermissions';
import currencyJs from 'currency.js';
import { eEscrowTypes } from '../../../../types/order/escrow/eEscrowTypes';
import { useHasPermissions } from '../../../accessControl/hooks/useHasPermissions';
import { PineconeTheme } from '../../../../ui-toolkit/theming/PineconeTheme';
import type { FCWithChildren } from '../../../../types/FCWithChildren';

interface IAddOutgoingButtonProps {
  amount: number;
  cdItemId: string;
  onHideCDItem: () => void;
  onAddTransaction: (type: eEscrowTypes, amount: number, cdItemId: string) => void;
}

interface WithAddProps {
  disabled?: boolean;
}

const WithAdd: FCWithChildren<WithAddProps> = ({ disabled, children }) => {
  return (
    <>
      <Icon type={IconType.Plus} size={IconSize.Small} color={!disabled ? undefined : PineconeTheme.colors.darkGray} />
      {children}
    </>
  );
};

export const SpaAddOutgoingButton: React.FC<IAddOutgoingButtonProps> = ({
  amount,
  cdItemId,
  onHideCDItem,
  onAddTransaction,
}) => {
  const hasWireDisbursementsEditPermissions = useHasPermissions([ePermissions.EscrowWireDisbursementsEdit]);
  const hasCheckDisbursementsEditPermissions = useHasPermissions([ePermissions.EscrowCheckDisbursementsEdit]);
  const hasManualChecksEditPermissions = useHasPermissions([ePermissions.EscrowManualCheckDisbursementsEdit]);
  const hasAchPushDepositsEditPermissions = useHasPermissions([ePermissions.EscrowExpectedAchPushDepositsEdit]);
  const hasInternalTransfersEditPermissions = useHasPermissions([ePermissions.EscrowExpectedInternalTransfersEdit]);
  const noOutgoingPermissions =
    !hasWireDisbursementsEditPermissions &&
    !hasCheckDisbursementsEditPermissions &&
    !hasManualChecksEditPermissions &&
    !hasAchPushDepositsEditPermissions &&
    !hasInternalTransfersEditPermissions;

  const buttonMenu = [
    {
      children: <WithAdd>Add Payment Manager Automated Wire</WithAdd>,
      onClick: () => {
        onAddTransaction(eEscrowTypes.PaymentManagerAutomatedWires, currencyJs(amount).multiply(100).value, cdItemId);
        onHideCDItem();
      },
    },
    {
      children: <WithAdd>Add Payment Manager Automated ACH Push</WithAdd>,
      onClick: () => {
        onAddTransaction(eEscrowTypes.PaymentManagerAutomatedACHPush, currencyJs(amount).multiply(100).value, cdItemId);
        onHideCDItem();
      },
    },
    {
      children: <WithAdd>Add Manual Check</WithAdd>,
      onClick: () => {
        onAddTransaction(eEscrowTypes.ManualCheck, currencyJs(amount).multiply(100).value, cdItemId);
        onHideCDItem();
      },
    },
    {
      children: <WithAdd>Add Internal Transfer</WithAdd>,
      onClick: () => {
        onAddTransaction(eEscrowTypes.InternalTransfer, currencyJs(amount).multiply(100).value, cdItemId);
        onHideCDItem();
      },
    },
  ];

  return noOutgoingPermissions ? (
    <Tooltip
      placement={'auto'}
      trigger={'hover'}
      tooltipContent={<BodyText>No Outgoing Escrow Edit Permissions</BodyText>}
    >
      <ButtonDropdown intent={'primary'} items={buttonMenu} disabled={true}>
        Select Method
      </ButtonDropdown>
    </Tooltip>
  ) : (
    <ButtonDropdown intent={'primary'} items={buttonMenu}>
      Select Method
    </ButtonDropdown>
  );
};
